import { Container } from 'pixi.js';
import { GameApplication } from '../application';
import { GameView } from '../game-view';
import { PanelLandscapeView } from './panel-landscape-view';
import { PanelPortraitView } from './panel-portrait-view';

export enum OrientationType {
    LANDSCAPE,
    PORTRAIT
}
export enum BuildType {
    LEFT,
    RIGHT,
    TOP
}
export interface ICollectorPanel {
    id: number;
    cells: number;
    key: string;
    buildType: BuildType;
}

export const collectorKey = ['lightBlue', 'blue', 'darkBlue', 'purple', 'yellow', 'orange', 'red'];

export class CollectorView extends Container {
    private _collectorPanelViews: PanelLandscapeView[] | PanelPortraitView[];
    private _orientationType: OrientationType;

    constructor(orientationType: OrientationType) {
        super();
        this._collectorPanelViews = [];
        this._orientationType = orientationType;
        this.buildCollectorPanels();
        this.positionCollectorPanels();
    }

    public draw(): void {
        this.positionCollectorPanels();
    }

    private buildCollectorPanels(): void {
        // Initialize collector panel configurations
        const cells: number[] = [2, 2, 3, 3, 4, 4, 5];
        const landscapeBuild = [BuildType.LEFT, BuildType.LEFT, BuildType.LEFT, BuildType.LEFT, BuildType.RIGHT, BuildType.RIGHT, BuildType.TOP];
        const portraitBuild = [BuildType.LEFT, BuildType.LEFT, BuildType.LEFT, BuildType.LEFT, BuildType.RIGHT, BuildType.RIGHT, BuildType.RIGHT];
        const buildType: BuildType[] = this._orientationType === OrientationType.LANDSCAPE ? landscapeBuild : portraitBuild;

        // Create collector panels and add them to the container
        for (let $collectorIndex = 0; $collectorIndex < cells.length; $collectorIndex++) {
            const prizePanelConfig: ICollectorPanel = {
                id: $collectorIndex,
                cells: cells[$collectorIndex],
                key: collectorKey[$collectorIndex],
                buildType: buildType[$collectorIndex]
            };
            this.createPanels(prizePanelConfig);
        }
    }

    private createPanels(prizePanelConfig: ICollectorPanel): void {
        // create panel
        const prizePanelView = this._orientationType === OrientationType.LANDSCAPE ? new PanelLandscapeView(prizePanelConfig) : new PanelPortraitView(prizePanelConfig);
        this.addChild(prizePanelView);
        this._collectorPanelViews.push(prizePanelView as never);
    }

    private positionCollectorPanels(): void {
        if (this.parent === null) {
            return;
        }

        // call predraw on all collectors
        this._collectorPanelViews.forEach((panel) => panel.preDraw());

        if (this._orientationType === OrientationType.LANDSCAPE) {
            this.landscapePosition();
        } else {
            this.portraitPosition();
        }

        // call postdraw on all collectors
        this._collectorPanelViews.forEach((panel) => panel.postDraw());
    }

    private portraitPosition(): void {
        const boundLeft = GameView.GAME_BOARD_BOUND.x / this.scale.x;
        const boundRight = boundLeft + GameView.GAME_BOARD_BOUND.width / this.scale.x;
        const boundTop = (GameApplication.pixiApp.renderer.height * 0.12) / this.scale.x;
        const padding = this._collectorPanelViews[0].width * 0.42;
        const spacingY = this._collectorPanelViews[0].height * 2.7;

        // Position individual collector panels
        for (let collectorIndex = 6; collectorIndex >= 0; collectorIndex--) {
            const collector = this._collectorPanelViews[collectorIndex];

            if (collectorIndex >= 3) {
                // Position on the left side
                collector.x = boundLeft - padding;
                collector.y = boundTop + (6 - collectorIndex) * (collector.height + spacingY);
            } else {
                // Position on the right side
                collector.x = boundRight + padding;
                collector.y = boundTop + (2 - collectorIndex) * (collector.height + spacingY);
            }
        }
    }

    private landscapePosition(): void {
        // Calculate positioning parameters
        const boundLeft = GameView.GAME_BOARD_BOUND.x / this.scale.x;
        const boundRight = boundLeft + GameView.GAME_BOARD_BOUND.width / this.scale.x;
        const boundTop = (GameApplication.pixiApp.renderer.height * 0.06) / this.scale.x;
        const padding = this._collectorPanelViews[0].width * 0.62;
        const spacingY = this._collectorPanelViews[0].height * 0.24;

        // Initialize the total height to keep track of the accumulated heights of panels
        let totalHeight = 0;

        // Position collector panels
        for (let collectorIndex = 0; collectorIndex < this._collectorPanelViews.length; collectorIndex++) {
            const collector = this._collectorPanelViews[collectorIndex];

            // Position panels based on their individual heights
            collector.x = boundLeft - padding;
            collector.y = boundTop + totalHeight;

            // Update the total height with the current panel's height and spacing
            totalHeight += collector.height + spacingY;
        }

        // Position collector panels to the right of the game board
        const rightStartX = boundRight + padding;
        this._collectorPanelViews[4].x = rightStartX;
        this._collectorPanelViews[4].y = boundTop;
        this._collectorPanelViews[5].x = rightStartX;
        this._collectorPanelViews[5].y = boundTop + this._collectorPanelViews[4].height + spacingY;

        // Position the center collector panel
        const centerX = (GameApplication.pixiApp.renderer.width * 0.5) / this.scale.x;
        this._collectorPanelViews[6].x = centerX;
        this._collectorPanelViews[6].y = boundTop;
    }
}
